import { useEffect, useRef } from "react";

/**
 * Spaceballs-style hyperspace warp.
 * Canvas-based for performance — pure white streaks against black.
 */
export default function WarpField({ density = 220, speed = 1, className = "" }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;
    let stars = [];
    let w = 0, h = 0, cx = 0, cy = 0;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      cx = w / 2;
      cy = h / 2;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const reset = (s) => {
      s.x = (Math.random() - 0.5) * w * 2;
      s.y = (Math.random() - 0.5) * h * 2;
      s.z = Math.random() * w;
      s.pz = s.z;
    };

    const init = () => {
      stars = Array.from({ length: density }, () => {
        const s = {};
        reset(s);
        return s;
      });
    };

    const tick = () => {
      ctx.fillStyle = "rgba(0,0,0,0.35)";
      ctx.fillRect(0, 0, w, h);
      ctx.strokeStyle = "#ffffff";
      ctx.lineCap = "round";

      for (const s of stars) {
        s.pz = s.z;
        s.z -= 8 * speed;
        if (s.z < 1) reset(s);

        const sx = (s.x / s.z) * 200 + cx;
        const sy = (s.y / s.z) * 200 + cy;
        const px = (s.x / s.pz) * 200 + cx;
        const py = (s.y / s.pz) * 200 + cy;

        const thickness = Math.max(0.4, 2.4 * (1 - s.z / w));
        ctx.lineWidth = thickness;
        ctx.globalAlpha = Math.min(1, 1.1 - s.z / w);
        ctx.beginPath();
        ctx.moveTo(px, py);
        ctx.lineTo(sx, sy);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(tick);
    };

    resize();
    init();
    tick();
    window.addEventListener("resize", () => { resize(); init(); });
    return () => cancelAnimationFrame(raf);
  }, [density, speed]);

  return (
    <canvas
      ref={canvasRef}
      className={`absolute inset-0 w-full h-full ${className}`}
      data-testid="warp-field"
    />
  );
}
